import raw from './gear.json';
import type { Product, ProductVariant, SelectedOption } from '@/lib/store/types';

/**
 * Ω GEAR — the merch catalogue (ΩΞ-TILL-MERCH-001). gear.json is the single source for what
 * /ohm-gear shows and what the till charges (the same file is deployed beside the checkout API).
 * Prices are cents in the file; a price that breaks the rule of three refuses to load.
 */
interface RawVariant {
  readonly id: string;
  readonly title: string;
  readonly price: number;
  readonly compareAtPrice: number | null;
  readonly available: boolean;
  readonly selectedOptions: readonly SelectedOption[];
  readonly image: string | null;
}

interface RawProduct {
  readonly handle: string;
  readonly title: string;
  readonly description: string;
  readonly descriptionHtml: string;
  readonly productType: string;
  readonly tags: readonly string[];
  readonly images: readonly { url: string; altText: string | null; width: number | null; height: number | null }[];
  readonly variants: readonly RawVariant[];
}

export class GearPriceError extends Error {
  override readonly name = 'GearPriceError';
}

function dollars(cents: number, what: string): number {
  if (!Number.isInteger(cents) || cents <= 0 || cents % 3 !== 0) {
    throw new GearPriceError(`${what}: ${String(cents)} is not a price in cents divisible by three`);
  }
  return cents / 100;
}

function toProduct(p: RawProduct): Product {
  const variants: ProductVariant[] = p.variants.map((v) => ({
    id: v.id,
    title: v.title,
    price: dollars(v.price, `${p.title} / ${v.title}`),
    compareAtPrice: v.compareAtPrice === null ? null : v.compareAtPrice / 100,
    available: v.available,
    selectedOptions: v.selectedOptions,
    image: v.image,
  }));
  const prices = variants.map((v) => v.price);
  return {
    id: `gear:${p.handle}`,
    handle: p.handle,
    title: p.title,
    description: p.description,
    descriptionHtml: p.descriptionHtml,
    images: p.images,
    variants,
    priceRange: { min: Math.min(...prices), max: Math.max(...prices) },
    tags: p.tags,
    productType: p.productType,
    availableForSale: variants.some((v) => v.available),
    vault: false,
  };
}

let products: readonly Product[] | null = null;

/** Every garment, in shelf order. */
export function gearProducts(): readonly Product[] {
  products ??= (raw.products as readonly RawProduct[]).map(toProduct);
  return products;
}
